import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiSearch,
  FiFileText,
  FiAlertCircle,
  FiEye,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiArchive,
  FiCheckCircle,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import {
  PERIODO_REGEX,
  listarBoletasFirmadas,
  partesPeriodo,
  tieneGratificacion,
  ultimoPeriodoDisponible,
} from "../services/boletasService";
import BoletaPdfViewer, { crearCachePdf } from "../components/Boletas/BoletaPdfViewer";
import { fmtFecha, fmtPeriodo, normalizarTexto } from "../components/Boletas/boletasFormat";
import { descargarEnBloque } from "../components/Boletas/descargaMasiva";
import MonthPicker from "../components/Boletas/MonthPicker";
import "../styles/boletasFirmadas.css";

const PAGE_SIZES = [10, 25, 50];

// Periodo inicial: el de ?periodo= (p. ej. desde el dashboard) si es válido
// y ya está disponible; si no, el último periodo disponible.
function periodoInicial(param) {
  const max = ultimoPeriodoDisponible();
  if (!PERIODO_REGEX.test(param || "")) return { base: max, gratificacion: false };
  const { base, gratificacion } = partesPeriodo(param);
  if (base > max) return { base: max, gratificacion: false };
  return { base, gratificacion: gratificacion && tieneGratificacion(base) };
}

// "2026-07" + gratificación -> "2026-07G"
const periodoConTipo = (base, gratificacion) => (gratificacion ? `${base}G` : base);

// [1, "gap", 4, 5, 6, "gap", 12]
function paginasVisibles(actual, totalPaginas) {
  if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);
  const nums = [...new Set([1, totalPaginas, actual - 1, actual, actual + 1])]
    .filter((n) => n >= 1 && n <= totalPaginas)
    .sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

const rowKey = (r) => `${r.persona}-${r.periodo}`;

// Checkbox con estado "indeterminado" (selección parcial).
function CheckboxTriple({ checked, indeterminate, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} {...props} />;
}

export default function BoletasFirmadas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inicial] = useState(() => periodoInicial(searchParams.get("periodo")));
  // `periodo` es siempre el mes base "AAAA-MM"; la gratificación va aparte.
  const [periodo, setPeriodo] = useState(inicial.base);
  const [gratificacion, setGratificacion] = useState(inicial.gratificacion);
  const [periodoBuscado, setPeriodoBuscado] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [modalDescargaAbierto, setModalDescargaAbierto] = useState(false);
  // null | { modo, estado: "running"|"done"|"cancelled", terminadas, total, ok, fallidas: [] }
  const [descarga, setDescarga] = useState(null);
  const descargaAbortRef = useRef(null);
  const modalDescargaRef = useRef(null);

  const abortRef = useRef(null);
  const cacheRef = useRef(null);
  if (!cacheRef.current) cacheRef.current = crearCachePdf();
  const focusKeyRef = useRef(null);
  const searchRef = useRef(null);

  const buscar = async (p) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setViewerIndex(null);
    descargaAbortRef.current?.abort();
    setDescarga(null);
    setSeleccion(new Set());
    cacheRef.current.clear();
    setLoading(true);
    setError("");
    setRows([]);
    setFiltro("");
    setPage(1);
    setPeriodoBuscado(p);
    try {
      const data = await listarBoletasFirmadas(p, { signal: controller.signal });
      setRows(data);
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Ocurrió un error al consultar las boletas.");
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  };

  useEffect(() => {
    const cache = cacheRef.current;
    buscar(periodoConTipo(inicial.base, inicial.gratificacion));
    return () => {
      abortRef.current?.abort();
      descargaAbortRef.current?.abort();
      cache.clear();
    };
    // Solo al montar: carga el último periodo disponible por defecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodoMax = ultimoPeriodoDisponible();

  const mesConGrati = tieneGratificacion(periodo);

  const consultar = (base, grati) => {
    const p = periodoConTipo(base, grati);
    setSearchParams({ periodo: p }, { replace: true });
    buscar(p);
  };

  // Cambiar de mes consulta automáticamente. En meses sin gratificación
  // se vuelve a la remuneración.
  const cambiarPeriodo = (p) => {
    const grati = gratificacion && tieneGratificacion(p);
    setPeriodo(p);
    setGratificacion(grati);
    consultar(p, grati);
  };

  const cambiarTipo = (grati) => {
    if (grati === gratificacion) return;
    setGratificacion(grati);
    consultar(periodo, grati);
  };

  // ---------- búsqueda + paginación (en cliente) ----------
  const filtradas = useMemo(() => {
    const terminos = normalizarTexto(filtro).split(/\s+/).filter(Boolean);
    if (!terminos.length) return rows;
    return rows.filter((r) => {
      const texto = normalizarTexto(`${r.persona} ${r.nombreCompleto}`);
      return terminos.every((t) => texto.includes(t));
    });
  }, [rows, filtro]);

  const total = rows.length;
  const totalFiltradas = filtradas.length;
  const totalPaginas = Math.max(1, Math.ceil(totalFiltradas / pageSize));
  const paginaActual = Math.min(page, totalPaginas);
  const inicio = (paginaActual - 1) * pageSize;
  const visibles = filtradas.slice(inicio, inicio + pageSize);
  const hasSearched = Boolean(periodoBuscado);
  const showResults = hasSearched && !loading && !error;
  const hayDatos = showResults && total > 0;

  // Tras cerrar el visor, devolver el foco al botón de la boleta que se estaba viendo.
  useEffect(() => {
    if (viewerIndex !== null || !focusKeyRef.current) return;
    const btn = document.querySelector(`[data-boleta-key="${CSS.escape(focusKeyRef.current)}"]`);
    focusKeyRef.current = null;
    btn?.focus();
  }, [viewerIndex, paginaActual]);

  const abrirVisor = (r) => setViewerIndex(filtradas.indexOf(r));

  const cerrarVisor = useCallback(() => {
    const r = viewerIndex !== null ? filtradas[viewerIndex] : null;
    if (r) {
      // Si se navegó a otra boleta dentro del visor, llevar la tabla a su página.
      focusKeyRef.current = rowKey(r);
      setPage(Math.floor(viewerIndex / pageSize) + 1);
    }
    setViewerIndex(null);
  }, [viewerIndex, filtradas, pageSize]);

  const irAPagina = (n) => setPage(Math.min(Math.max(1, n), totalPaginas));

  // ---------- selección y descarga en bloque ----------
  const seleccionadas = useMemo(() => rows.filter((r) => seleccion.has(rowKey(r))), [rows, seleccion]);
  // El checkbox de la cabecera actúa solo sobre las filas de la página actual.
  const nSelPagina = visibles.reduce((n, r) => n + (seleccion.has(rowKey(r)) ? 1 : 0), 0);
  const paginaCompletaSel = visibles.length > 0 && nSelPagina === visibles.length;

  // Sin selección se descarga lo que muestra la tabla (todas, o las filtradas).
  const objetivo = seleccionadas.length ? seleccionadas : filtradas;
  const etiquetaObjetivo = seleccionadas.length
    ? `seleccionadas (${seleccionadas.length})`
    : filtro
      ? `filtradas (${filtradas.length})`
      : `todas (${filtradas.length})`;
  const descargando = descarga?.estado === "running";

  const toggleFila = (r) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const togglePagina = () =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      visibles.forEach((r) => (paginaCompletaSel ? next.delete(rowKey(r)) : next.add(rowKey(r))));
      return next;
    });

  const iniciarDescarga = async (modo, items = objetivo) => {
    if (!items.length || descargando) return;
    let directorio;
    if (modo === "carpeta") {
      if (!window.showDirectoryPicker) modo = "sueltas";
      else {
        try {
          directorio = await window.showDirectoryPicker({ mode: "readwrite" });
        } catch (err) {
          if (err?.name === "AbortError") return;
          setError(err?.message || "No se pudo seleccionar la carpeta.");
          return;
        }
      }
    }
    descargaAbortRef.current?.abort();
    const controller = new AbortController();
    descargaAbortRef.current = controller;

    setDescarga({ modo, estado: "running", terminadas: 0, total: items.length, ok: 0, fallidas: [] });
    try {
      const { ok, fallidas } = await descargarEnBloque(items, {
        cache: cacheRef.current,
        modo,
        directorio,
        nombreZip: `boletas_firmadas_${periodoBuscado}.zip`,
        signal: controller.signal,
        onProgress: ({ terminadas }) =>
          setDescarga((d) => (d && d.estado === "running" ? { ...d, terminadas } : d)),
      });
      setDescarga({ modo, estado: "done", terminadas: items.length, total: items.length, ok: ok.length, fallidas });
    } catch (err) {
      if (err?.name !== "AbortError") {
        setDescarga({
          modo,
          estado: "done",
          terminadas: 0,
          total: items.length,
          ok: 0,
          fallidas: items.map((item) => ({ item, error: err })),
        });
      } else if (descargaAbortRef.current === controller) {
        setDescarga((d) => (d ? { ...d, estado: "cancelled" } : d));
      }
    }
  };

  const limpiarFiltro = () => {
    setFiltro("");
    setPage(1);
    searchRef.current?.focus();
  };

  useEffect(() => {
    if (modalDescargaAbierto && !modalDescargaRef.current?.open) modalDescargaRef.current?.showModal();
  }, [modalDescargaAbierto]);

  return (
    <div className="boletas-page">
      <header className="boletas-header">
        <h1 className="boletas-title">Boletas firmadas</h1>
        <p className="boletas-subtitle">
          Consulte las boletas de pago confirmadas por el personal en un periodo mensual.
        </p>
      </header>

      <section className="boletas-card boletas-results" aria-labelledby="boletas-result-title" aria-busy={loading}>
        <h2 id="boletas-result-title" className="sr-only">
          Boletas firmadas de {fmtPeriodo(periodoConTipo(periodo, gratificacion))}
        </h2>

        <div className="boletas-toolbar">
          <div className="boletas-toolbar__left">
            <span id="boletas-periodo-label" className="sr-only">
              Periodo
            </span>
            <MonthPicker
              id="boletas-periodo"
              labelId="boletas-periodo-label"
              value={periodo}
              max={periodoMax}
              onChange={cambiarPeriodo}
            />

            {mesConGrati && (
              <div className="boletas-tipo" role="group" aria-label="Tipo de boleta">
                <button
                  type="button"
                  className={`boletas-tipo__btn ${!gratificacion ? "is-active" : ""}`}
                  aria-pressed={!gratificacion}
                  onClick={() => cambiarTipo(false)}
                >
                  Remuneración
                </button>
                <button
                  type="button"
                  className={`boletas-tipo__btn ${gratificacion ? "is-active" : ""}`}
                  aria-pressed={gratificacion}
                  onClick={() => cambiarTipo(true)}
                >
                  Gratificación
                </button>
              </div>
            )}

          </div>

          {hayDatos && (
            <div className="boletas-toolbar__right" role="group" aria-label="Descarga en bloque">
              <button
                type="button"
                className="boletas-btn"
                onClick={() => setModalDescargaAbierto(true)}
                disabled={descargando || !objetivo.length}
                aria-haspopup="dialog"
              >
                <FiDownload aria-hidden="true" /> Descargar ({objetivo.length})
              </button>
            </div>
          )}

          <div className="boletas-search">
            <label htmlFor="boletas-filtro" className="sr-only">
              Buscar por documento o nombre
            </label>
            <FiSearch className="boletas-search__icon" aria-hidden="true" />
            <input
              ref={searchRef}
              id="boletas-filtro"
              type="search"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por documento o nombre…"
              autoComplete="off"
              spellCheck="false"
              aria-controls="boletas-tabla"
              disabled={!hayDatos}
            />
            {filtro && (
              <button
                type="button"
                className="boletas-search__clear"
                onClick={limpiarFiltro}
                aria-label="Limpiar búsqueda"
                title="Limpiar búsqueda"
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {!hasSearched && !loading && (
          <div className="boletas-state">
            <FiFileText className="boletas-state-icon" aria-hidden="true" />
            <p>Seleccione un periodo para ver las boletas firmadas.</p>
          </div>
        )}

        {loading && (
          <div className="boletas-state" role="status" aria-live="polite">
            <span className="boletas-spinner" aria-hidden="true" />
            <p>Cargando boletas firmadas…</p>
          </div>
        )}

        {!loading && error && (
          <div className="boletas-alert" role="alert">
            <FiAlertCircle aria-hidden="true" />
            <div>
              <strong>No se pudo cargar la información.</strong>
              <p>{error}</p>
            </div>
            <button type="button" className="boletas-btn boletas-btn--ghost" onClick={() => buscar(periodoBuscado)}>
              Reintentar
            </button>
          </div>
        )}

        {showResults && total === 0 && (
          <div className="boletas-state">
            <FiFileText className="boletas-state-icon" aria-hidden="true" />
            <p>No hay boletas firmadas para este periodo</p>
          </div>
        )}

        {hayDatos && (
          <>
            {seleccion.size > 0 && (
              <div className="boletas-summary-row">
                <p className="boletas-summary" aria-live="polite">
                  <strong>{seleccion.size} seleccionada{seleccion.size === 1 ? "" : "s"}</strong>{" "}
                  <button type="button" className="boletas-link" onClick={() => setSeleccion(new Set())}>
                    Quitar selección
                  </button>
                </p>
              </div>
            )}

            {descarga && (
              <div
                className={`boletas-progress ${descarga.estado === "done" && descarga.fallidas.length ? "has-errors" : ""}`}
                role={descarga.estado === "running" ? "status" : undefined}
                aria-live="polite"
              >
                {descarga.estado === "running" && (
                  <>
                    <div className="boletas-progress__row">
                      <span>
                        {descarga.modo === "zip" ? "Preparando el .zip" : descarga.modo === "carpeta" ? "Guardando PDFs" : "Descargando PDFs"}: {descarga.terminadas} de {descarga.total}
                      </span>
                      <button type="button" className="boletas-link" onClick={() => descargaAbortRef.current?.abort()}>
                        Cancelar
                      </button>
                    </div>
                    <progress value={descarga.terminadas} max={descarga.total} aria-label="Progreso de la descarga" />
                    {descarga.modo === "sueltas" && (
                      <p className="boletas-progress__hint">
                        Si el navegador pregunta si permite descargar varios archivos, elija <strong>Permitir</strong>.
                      </p>
                    )}
                  </>
                )}

                {descarga.estado === "cancelled" && (
                  <div className="boletas-progress__row">
                    <span>Descarga cancelada.</span>
                    <button type="button" className="boletas-link" onClick={() => setDescarga(null)}>
                      Cerrar
                    </button>
                  </div>
                )}

                {descarga.estado === "done" && (
                  <>
                    <div className="boletas-progress__row">
                      <span className="boletas-progress__result">
                        {descarga.fallidas.length ? (
                          <FiAlertCircle aria-hidden="true" />
                        ) : (
                          <FiCheckCircle aria-hidden="true" />
                        )}
                        {descarga.ok
                          ? `${descarga.modo === "zip" ? "Se generó el .zip con" : descarga.modo === "carpeta" ? "Se guardaron" : "Se descargaron"} ${descarga.ok} de ${descarga.total} boletas.`
                          : "No se pudo descargar ninguna boleta."}
                      </span>
                      <button
                        type="button"
                        className="boletas-search__clear boletas-progress__close"
                        onClick={() => setDescarga(null)}
                        aria-label="Cerrar aviso"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                    {descarga.fallidas.length > 0 && (
                      <>
                        <p className="boletas-progress__hint">No se pudieron obtener:</p>
                        <ul className="boletas-progress__list">
                          {descarga.fallidas.map(({ item, error: e }) => (
                            <li key={rowKey(item)}>
                              {item.nombreCompleto || "-"} ({item.persona}) — {e?.message || "error"}
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className="boletas-btn boletas-btn--sm boletas-btn--ghost"
                          onClick={() => iniciarDescarga(descarga.modo, descarga.fallidas.map((f) => f.item))}
                        >
                          Reintentar las fallidas
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {totalFiltradas === 0 ? (
              <div className="boletas-state">
                <FiSearch className="boletas-state-icon" aria-hidden="true" />
                <p>Ninguna boleta coincide con «{filtro}».</p>
                <button type="button" className="boletas-btn boletas-btn--sm boletas-btn--ghost" onClick={limpiarFiltro}>
                  Limpiar búsqueda
                </button>
              </div>
            ) : (
              <div className="boletas-table-wrap">
                <table id="boletas-tabla" className="boletas-table">
                  <caption className="sr-only">
                    Boletas firmadas del periodo {fmtPeriodo(periodoBuscado)}, página {paginaActual} de {totalPaginas}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className="boletas-col-check">
                        <CheckboxTriple
                          checked={paginaCompletaSel}
                          indeterminate={nSelPagina > 0 && !paginaCompletaSel}
                          onChange={togglePagina}
                          aria-label={
                            paginaCompletaSel
                              ? "Quitar la selección de las boletas de esta página"
                              : `Seleccionar las ${visibles.length} boletas de esta página`
                          }
                          title={paginaCompletaSel ? "Quitar selección de esta página" : "Seleccionar esta página"}
                        />
                      </th>
                      <th scope="col">Documento</th>
                      <th scope="col">Nombre completo</th>
                      <th scope="col">Periodo</th>
                      <th scope="col">Fecha de verificación</th>
                      <th scope="col" className="boletas-col-action">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((r) => (
                      <tr key={rowKey(r)} className={seleccion.has(rowKey(r)) ? "is-selected" : undefined}>
                        <td data-label="Seleccionar" className="boletas-col-check">
                          <input
                            type="checkbox"
                            checked={seleccion.has(rowKey(r))}
                            onChange={() => toggleFila(r)}
                            aria-label={`Seleccionar boleta de ${r.nombreCompleto || r.persona}`}
                          />
                        </td>
                        <td data-label="Documento" className="boletas-mono">{r.persona || "-"}</td>
                        <td data-label="Nombre completo">{r.nombreCompleto || "-"}</td>
                        <td data-label="Periodo">{fmtPeriodo(r.periodo)}</td>
                        <td data-label="Fecha de verificación">
                          <time dateTime={r.fechaConfirmacion.replace(" ", "T")}>
                            {fmtFecha(r.fechaConfirmacion)}
                          </time>
                        </td>
                        <td data-label="Acción" className="boletas-col-action">
                          {r.persona ? (
                            <button
                              type="button"
                              className="boletas-btn boletas-btn--sm"
                              data-boleta-key={rowKey(r)}
                              onClick={() => abrirVisor(r)}
                              aria-haspopup="dialog"
                              aria-label={`Ver PDF de la boleta de ${r.nombreCompleto || r.persona}`}
                            >
                              <FiEye aria-hidden="true" />
                              Ver
                            </button>
                          ) : (
                            <span className="boletas-muted">PDF no disponible</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalFiltradas > 0 && (
              <div className="boletas-footer">
                <p className="boletas-summary" aria-live="polite">
                  Mostrando {inicio + 1}–{inicio + visibles.length} de {totalFiltradas}
                  {filtro && totalFiltradas !== total && ` (filtrado de ${total})`}
                </p>

                <div className="boletas-pagesize">
                  <label htmlFor="boletas-pagesize">Filas</label>
                  <select
                    id="boletas-pagesize"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                {totalPaginas > 1 && (
                  <nav className="boletas-pagination" aria-label="Paginación de boletas">
                    <button
                      type="button"
                      className="bol-page-btn"
                      onClick={() => irAPagina(paginaActual - 1)}
                      disabled={paginaActual === 1}
                      aria-label="Página anterior"
                    >
                      <FiChevronLeft aria-hidden="true" />
                      <span className="bol-page-btn__text">Anterior</span>
                    </button>

                    <ul className="boletas-pagination__list">
                      {paginasVisibles(paginaActual, totalPaginas).map((n) =>
                        typeof n === "string" ? (
                          <li key={n} className="boletas-pagination__gap" aria-hidden="true">…</li>
                        ) : (
                          <li key={n}>
                            <button
                              type="button"
                              className={`bol-page-btn ${n === paginaActual ? "is-active" : ""}`}
                              onClick={() => irAPagina(n)}
                              aria-label={`Página ${n}`}
                              aria-current={n === paginaActual ? "page" : undefined}
                            >
                              {n}
                            </button>
                          </li>
                        )
                      )}
                    </ul>

                    <span className="boletas-pagination__compact" aria-hidden="true">
                      {paginaActual} / {totalPaginas}
                    </span>

                    <button
                      type="button"
                      className="bol-page-btn"
                      onClick={() => irAPagina(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                      aria-label="Página siguiente"
                    >
                      <span className="bol-page-btn__text">Siguiente</span>
                      <FiChevronRight aria-hidden="true" />
                    </button>
                  </nav>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {viewerIndex !== null && filtradas[viewerIndex] && (
        <BoletaPdfViewer
          items={filtradas}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={cerrarVisor}
          cache={cacheRef.current}
        />
      )}

      {modalDescargaAbierto && (
        <dialog
          ref={modalDescargaRef}
          className="boletas-download-modal"
          aria-labelledby="boletas-download-title"
          onCancel={(e) => {
            e.preventDefault();
            setModalDescargaAbierto(false);
          }}
        >
          <div className="boletas-download-modal__header">
            <h2 id="boletas-download-title">Descargar boletas</h2>
            <button type="button" className="bol-icon-btn" onClick={() => setModalDescargaAbierto(false)} aria-label="Cerrar">
              <FiX aria-hidden="true" />
            </button>
          </div>
          <p>Descargará {etiquetaObjetivo}. Elija el formato:</p>
          <div className="boletas-download-modal__actions">
            <button type="button" className="boletas-btn" onClick={() => { setModalDescargaAbierto(false); iniciarDescarga("zip"); }}>
              <FiArchive aria-hidden="true" /> ZIP
            </button>
            <button type="button" className="boletas-btn boletas-btn--ghost" onClick={() => { setModalDescargaAbierto(false); iniciarDescarga("carpeta"); }}>
              <FiDownload aria-hidden="true" /> PDFs separados
            </button>
          </div>
        </dialog>
      )}
    </div>
  );
}
